import { Component } from 'react'
import { Button, Spinner } from './ui'

/** Detects a failed lazy() dynamic import — happens when the Vite dev server
 *  restarts (or a new build ships) while a tab holds the old module graph, so
 *  the chunk URL 404s. React re-throws the rejected promise into this boundary. */
function isChunkError(err) {
  const msg = String(err?.message || err || '')
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /'?text\/html'? is not a valid JavaScript MIME type/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  )
}

/**
 * Error boundary for lazy route content. On a stale-chunk error it hard-reloads
 * ONCE (guarded so it can't loop) to fetch the fresh module graph. Any other
 * render error shows an inline retry card instead of a blank white screen.
 */
export class RouteError extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, reloading: false }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error) {
    if (isChunkError(error)) {
      // Guard against reload loops: only auto-reload if we haven't just tried.
      const KEY = 'kx:chunk-reload'
      const last = Number(sessionStorage.getItem(KEY) || 0)
      const now = performance.now()
      if (now - last > 10000) {
        sessionStorage.setItem(KEY, String(now))
        this.setState({ reloading: true })
        window.location.reload()
      }
    }
  }

  reset = () => {
    this.setState({ error: null, reloading: false })
    if (this.props.onReset) this.props.onReset()
  }

  render() {
    const { error, reloading } = this.state
    if (!error) return this.props.children

    if (reloading || isChunkError(error)) {
      return (
        <div className="py-24 grid place-items-center text-muted gap-3">
          <Spinner size={26} />
          <p className="text-sm">Memuat versi terbaru…</p>
        </div>
      )
    }

    return (
      <div className="max-w-md mx-auto py-24 text-center">
        <p className="text-lg font-extrabold">Terjadi kesalahan saat memuat halaman</p>
        <p className="text-muted text-sm mt-1 kx-pretty">
          {String(error?.message || error).slice(0, 200)}
        </p>
        <div className="flex gap-2 justify-center mt-5">
          <Button icon="refresh" onClick={() => window.location.reload()}>Muat ulang</Button>
          <Button kind="outline" onClick={this.reset}>Coba lagi</Button>
        </div>
      </div>
    )
  }
}
